/**
 * Opportunity Solution Tree Types
 * 
 * Based on Teresa Torres' Continuous Discovery Habits framework:
 * - Outcome: The business goal/metric the team is trying to achieve
 * - Opportunity: Customer needs, pain points, or desires
 * - Solution: Product features, services, or improvements
 * - Experiment: Tests to validate solutions (assumption tests)
 */

export type NodeType = 'outcome' | 'opportunity' | 'solution' | 'experiment';

export type OpportunitySource = 
  | 'customer_interview'
  | 'support_ticket'
  | 'sales_conversation'
  | 'analytics'
  | 'survey'
  | 'user_research'
  | 'assumption';

export type ExperimentType = 
  | 'ab_test'
  | 'prototype_test'
  | 'fake_door'
  | 'concierge'
  | 'wizard_of_oz'
  | 'landing_page'
  | 'user_interview'
  | 'survey'
  | 'mvp';

export type ExperimentStatus = 
  | 'planned'
  | 'in_progress'
  | 'completed'
  | 'inconclusive';

export type ValidationStatus = 
  | 'not_started'
  | 'in_progress'
  | 'validated'
  | 'invalidated'
  | 'needs_more_data';

export interface BaseNode {
  id: string;
  type: NodeType;
  title: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  parentId: string | null;
  position?: { x: number; y: number };
  collapsed?: boolean;
  /** FK to items.id — set for opportunity and solution nodes. */
  itemId?: string | null;
}

export interface OutcomeNode extends BaseNode {
  type: 'outcome';
  metric?: string;
  target?: string;
  timeframe?: string;
  currentValue?: string;
  parentId: null; // Outcome is always the root
}

export interface OpportunityNode extends BaseNode {
  type: 'opportunity';
  source: OpportunitySource;
  customerQuote?: string;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'occasionally' | 'rare';
  severity?: 'critical' | 'high' | 'medium' | 'low';
  customerSegments?: string[];
  isTargeted?: boolean; // Is this the current target opportunity?
  parentId: string; // Parent is outcome or another opportunity
}

export interface SolutionNode extends BaseNode {
  type: 'solution';
  validationStatus: ValidationStatus;
  effort?: 'xs' | 'small' | 'medium' | 'large' | 'xl';
  confidence?: number; // 0-100
  assumptions?: string[];
  parentId: string; // Parent is an opportunity
}

export interface ExperimentNode extends BaseNode {
  type: 'experiment';
  experimentType: ExperimentType;
  status: ExperimentStatus;
  hypothesis?: string;
  successCriteria?: string;
  results?: string;
  learnings?: string;
  startDate?: string;
  endDate?: string;
  parentId: string; // Parent is a solution
}

export type OSTNode = OutcomeNode | OpportunityNode | SolutionNode | ExperimentNode;

export interface OpportunitySolutionTree {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  nodes: OSTNode[];
  metadata?: {
    teamName?: string;
    productArea?: string;
    quarter?: string;
    lastInterviewDate?: string;
    interviewCount?: number;
  };
}

// For the tree visualization
export interface TreeNodeWithChildren {
  node: OSTNode;
  children: TreeNodeWithChildren[];
  depth: number;
  isTargeted?: boolean;
}

// For import/export
export interface OSTExport {
  version: string;
  exportedAt: string;
  tree: OpportunitySolutionTree;
}

// For the assessment/prioritization of opportunities
export interface OpportunityAssessment {
  opportunityId: string;
  sizing: {
    customersAffected: 'many' | 'some' | 'few';
    frequency: 'often' | 'sometimes' | 'rarely';
  };
  marketFactors: {
    competitiveAdvantage: 'high' | 'medium' | 'low';
    marketTrend: 'growing' | 'stable' | 'declining';
  };
  companyFactors: {
    strategicAlignment: 'high' | 'medium' | 'low';
    missionSupport: 'high' | 'medium' | 'low';
  };
  customerFactors: {
    importance: 'critical' | 'important' | 'nice_to_have';
    satisfactionWithAlternatives: 'very_satisfied' | 'satisfied' | 'neutral' | 'dissatisfied';
  };
  overallScore?: number;
  notes?: string;
}

// Sample data for demo purposes
export const SAMPLE_TREE: OpportunitySolutionTree = {
  id: 'sample-ost-1',
  name: 'Increase User Engagement',
  description: 'Q1 2026 Discovery - Focused on improving daily active user engagement',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  metadata: {
    teamName: 'Growth Team',
    productArea: 'Core Platform',
    quarter: 'Q1 2026',
    interviewCount: 12,
    lastInterviewDate: new Date().toISOString(),
  },
  nodes: [
    {
      id: 'outcome-1',
      type: 'outcome',
      title: 'Increase daily active users by 20%',
      description: 'Drive more users to engage with the platform daily',
      metric: 'DAU / MAU ratio',
      target: '45%',
      currentValue: '38%',
      timeframe: 'Q1 2026',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentId: null,
    },
    {
      id: 'opp-1',
      type: 'opportunity',
      title: 'Users struggle to find relevant content',
      description: 'Users report spending too much time searching for content that matches their interests',
      source: 'customer_interview',
      customerQuote: "I spend 10 minutes scrolling before I find something worth reading",
      frequency: 'daily',
      severity: 'high',
      customerSegments: ['power users', 'casual users'],
      isTargeted: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentId: 'outcome-1',
    },
    {
      id: 'opp-2',
      type: 'opportunity',
      title: 'Users forget to check the app',
      description: 'Users have intention to use the app but forget during their day',
      source: 'survey',
      frequency: 'weekly',
      severity: 'medium',
      customerSegments: ['casual users'],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentId: 'outcome-1',
    },
    {
      id: 'opp-1-1',
      type: 'opportunity',
      title: 'Search results are not personalized',
      description: 'Generic search results don\'t account for user preferences',
      source: 'customer_interview',
      frequency: 'daily',
      severity: 'high',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentId: 'opp-1',
    },
    {
      id: 'sol-1',
      type: 'solution',
      title: 'AI-powered content recommendations',
      description: 'Use machine learning to suggest content based on user behavior and preferences',
      validationStatus: 'in_progress',
      effort: 'large',
      confidence: 70,
      assumptions: [
        'Users will trust AI recommendations',
        'We have enough data to train the model',
        'Recommendations will be relevant within 2 weeks of use',
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentId: 'opp-1',
    },
    {
      id: 'sol-2',
      type: 'solution',
      title: 'Curated topic feeds',
      description: 'Let users subscribe to curated topic feeds maintained by editors',
      validationStatus: 'not_started',
      effort: 'medium',
      confidence: 60,
      assumptions: [
        'Users prefer human curation over algorithms',
        'We can maintain quality at scale',
      ],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentId: 'opp-1',
    },
    {
      id: 'sol-3',
      type: 'solution',
      title: 'Improved search filters',
      description: 'Add advanced filtering options to help users narrow down results',
      validationStatus: 'validated',
      effort: 'small',
      confidence: 85,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentId: 'opp-1',
    },
    {
      id: 'exp-1',
      type: 'experiment',
      title: 'Fake door test for AI recommendations',
      experimentType: 'fake_door',
      status: 'completed',
      hypothesis: 'At least 30% of users will click on "Show AI Recommendations" button',
      successCriteria: '30% click rate',
      results: '42% of users clicked',
      learnings: 'Strong interest confirmed. Users also mentioned wanting control over recommendations.',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentId: 'sol-1',
    },
    {
      id: 'exp-2',
      type: 'experiment',
      title: 'Prototype test with 10 users',
      experimentType: 'prototype_test',
      status: 'in_progress',
      hypothesis: 'Users will find relevant content 50% faster with recommendations',
      successCriteria: 'Time to first meaningful engagement reduced by 50%',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentId: 'sol-1',
    },
  ],
};
